import React, { useState, useEffect, useRef } from "react";
import {
  Offcanvas,
  Alert,
  Button,
  Form,
  Spinner,
  Row,
  Col,
} from "react-bootstrap";
import { jwtDecode } from "jwt-decode";
import getNotificationService from "../../../services/getNotificationService";
import { getCookie } from "../../../function";
import { MdDone } from "react-icons/md";
import moment from "moment";
import { useNavigate } from "react-router-dom";

const NotificationPanel = ({ show, onHide, setUnreadCount }) => {
  const [token, setToken] = useState(getCookie("token"));
  const [isAdmin, setIsAdmin] = useState(false);

  const [filterVal, setFilterVal] = useState({
    id: getCookie("id"),
    fromDate: "",
    toDate: "",
    page: 1,
    limit: 5,
  });

  const [notifications, setNotifications] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const decodedToken = token ? jwtDecode(token) : null;

  useEffect(() => {
    if (decodedToken?.user?.userType) {
      setIsAdmin(decodedToken.user.userType === "admin");
    }
  }, [decodedToken]);

  const fetchNotifications = async () => {
    setLoading(true);
    try {
      const data = await getNotificationService.GetAllNotification(
        filterVal.id,
        {
          fromDate: filterVal.fromDate,
          toDate: filterVal.toDate,
          page: filterVal.page,
          limit: filterVal.limit,
        }
      );
      if (data) {
        setNotifications(data.notifications || []);
        setTotal(data.total || 0);
        setUnreadCount(data.unreadCount || 0);
      } else {
        setNotifications([]);
        setTotal(0);
      }
    } catch (error) {
      console.error("Error fetching notifications:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchNotifications();
  }, [filterVal.page, filterVal.fromDate, filterVal.toDate]);

  useEffect(() => {
    const intervalId = setInterval(() => {
      fetchNotifications();
    }, 100000);
    // Cleanup interval on component unmount
    return () => clearInterval(intervalId);
  }, []);

  const handleRemove = async (notificationId) => {
    try {
      await getNotificationService.DeleteNotification(notificationId);
      fetchNotifications();
    } catch (error) {
      console.error("Error removing notification:", error);
    }
  };

  const handleMarkAsComplete = async (notificationId) => {
    try {
      await getNotificationService.markAsComplete(notificationId);
      fetchNotifications();
    } catch (error) {
      console.error("Error marking as complete:", error);
    }
  };

  const handleDateChange = (e) => {
    const { name, value } = e.target;
    setFilterVal((prev) => ({ ...prev, [name]: value, page: 1 }));
  };

  const handleClearFilter = () => {
    setFilterVal((prev) => ({
      ...prev,
      fromDate: "",
      toDate: "",
      page: 1,
    }));
  };

  const handelAdminRedirect = async (id) => {
    onHide();
    navigate(`/admin/single-article/${id}`);
  };

  const handelEditorRedirect = async (id) => {
    onHide();
    navigate(`/admin/submitted-article/${id}`);
  };

  const handlePageChange = (direction) => {
    setFilterVal((prev) => ({
      ...prev,
      page: Math.max(1, prev.page + direction),
    }));
  };

  const totalPages = Math.ceil(total / filterVal.limit);

  const markNotificationAsSeen = async (notificationId) => {
    try {
      const userId = getCookie("id");
      console.log("Marking notification as seen:", notificationId, userId);
      await getNotificationService.markNotificationAsSeen(
        notificationId,
        userId
      );
      // fetchNotifications();
    } catch (error) {
      console.error("Error marking notification as seen:", error);
    }
  };

  const notificationRefs = useRef({});

  useEffect(() => {
    if (!show) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const notificationId = entry.target.dataset.notificationId;
            const notification = notifications.find(
              (n) => n.id === notificationId
            );

            if (notification) {
              if (isAdmin && !notification.seenByAdmin) {
                console.log("Admin notification in view:", notificationId);
                markNotificationAsSeen(notificationId);
              } else if (!isAdmin && !notification.seen) {
                console.log("User notification in view:", notificationId);
                markNotificationAsSeen(notificationId);
              }
            }
          }
        });
      },
      {
        root: null,
        rootMargin: "0px",
        threshold: 0.1,
      }
    );

    setTimeout(() => {
      Object.values(notificationRefs.current).forEach((element) => {
        if (element) {
          console.log("Observing element:", element.dataset.notificationId);
          observer.observe(element);
        }
      });
    }, 100);

    return () => {
      observer.disconnect();
    };
  }, [show, notifications]);

  return (
    <Offcanvas
      show={show}
      onHide={onHide}
      placement="end"
      scroll
      style={{ width: "50%" }}
    >
      <Offcanvas.Header closeButton>
        <Offcanvas.Title>Admin Notifications</Offcanvas.Title>
      </Offcanvas.Header>
      <Offcanvas.Body className="d-flex flex-column">
        {/* Date Filter Section */}
        <Row className="mb-3">
          <Col xs={12} sm={6}>
            <Form.Group>
              <Form.Label>From</Form.Label>
              <Form.Control
                type="date"
                name="fromDate"
                value={filterVal.fromDate}
                onChange={handleDateChange}
              />
            </Form.Group>
          </Col>
          <Col xs={12} sm={6}>
            <Form.Group>
              <Form.Label>To</Form.Label>
              <Form.Control
                type="date"
                name="toDate"
                value={filterVal.toDate}
                onChange={handleDateChange}
              />
            </Form.Group>
          </Col>
        </Row>
        <div className="d-flex justify-content-end mb-3">
          <Button
            variant="outline-secondary"
            size="sm"
            onClick={handleClearFilter}
          >
            Clear Filter
          </Button>
        </div>

        <hr />

        {/* Notification List */}
        {loading ? (
          <div className="text-center">
            <Spinner animation="border" variant="primary" />
          </div>
        ) : notifications.length > 0 ? (
          notifications.map((notif) => {
            return isAdmin ? (
              <Alert
                key={notif.id}
                variant="light"
                className="border border-primary mb-3 shadow-sm p-3 rounded"
                style={{ fontSize: "15px", backgroundColor: "#f9f9ff" }}
                ref={(el) => {
                  if (el) {
                    notificationRefs.current[notif.id] = el;
                    el.dataset.notificationId = notif.id;
                  }
                }}
              >
                <div className="d-flex justify-content-between align-items-start flex-column flex-sm-row">
                  {/* Message and timestamp */}
                  <div className="me-sm-auto mb-3 mb-sm-0">
                    <div
                      style={{
                        fontWeight: "600",
                        color: "#333",
                        fontSize: "16px",
                      }}
                      onClick={(e) => handelAdminRedirect(notif.articleId)}
                    >
                      {notif.message}
                    </div>
                    <div
                      style={{
                        fontSize: "13px",
                        color: "#666",
                        marginTop: "4px",
                      }}
                    >
                      Created: {moment(notif.createdAt).format("DD-MM-YY hh:mm A")}
                    </div>
                  </div>

                  {/* Status Badges */}
                  <div className="d-flex gap-3 flex-wrap justify-content-end">
                    <span
                      className={`badge px-3 py-2 rounded-pill fs-6 ${
                        notif.seen
                          ? "bg-success text-white"
                          : "bg-warning text-dark"
                      }`}
                      style={{ minWidth: "110px", textAlign: "center" }}
                    >
                      {notif.seen ? "Seen" : "Not Seen"}
                    </span>
                    <span
                      className={`badge px-3 py-2 rounded-pill fs-6 ${
                        notif.completed ? "bg-primary" : "bg-secondary"
                      }`}
                      style={{ minWidth: "110px", textAlign: "center" }}
                    >
                      {notif.completed ? "Completed" : "Pending"}
                    </span>
                  </div>
                </div>
              </Alert>
            ) : (
              <Alert
                key={notif.id}
                variant={notif.completed ? "success" : "primary"}
                className="d-flex justify-content-between align-items-center flex-column flex-sm-row mb-2"
                style={{
                  fontSize: "14px",
                  wordWrap: "break-word",
                }}
                ref={(el) => {
                  if (el) {
                    notificationRefs.current[notif.id] = el;
                    el.dataset.notificationId = notif.id;
                  }
                }}
              >
                <div className="text-start w-100">
                  <strong
                    onClick={(e) => handelEditorRedirect(notif.articleId)}
                  >
                    {notif.message}
                  </strong>

                  <div style={{ fontSize: "12px", color: "#666" }}>
                    {moment(notif.createdAt).format("DD-MM-YY HH:mm")}
                  </div>
                </div>

                <div className="mt-2 mt-sm-0 ms-sm-2">
                  {notif.completed ? (
                    <Button
                      variant="outline-light"
                      size="sm"
                      onClick={() => handleRemove(notif.id)}
                    >
                      &times;
                    </Button>
                  ) : (
                    <Button
                      variant="outline-light"
                      size="sm"
                      onClick={() => handleMarkAsComplete(notif.id)}
                    >
                      <MdDone />
                    </Button>
                  )}
                </div>
              </Alert>
            );
          })
        ) : (
          <p>No notifications at this time.</p>
        )}

        {/* Pagination */}
        <div className="mt-auto d-flex justify-content-between align-items-center">
          <Button
            variant="outline-primary"
            disabled={filterVal.page <= 1}
            onClick={() => handlePageChange(-1)}
          >
            Prev
          </Button>
          <span>
            Page {filterVal.page} of {totalPages}
          </span>
          <Button
            variant="outline-primary"
            disabled={filterVal.page >= totalPages}
            onClick={() => handlePageChange(1)}
          >
            Next
          </Button>
        </div>
      </Offcanvas.Body>
    </Offcanvas>
  );
};

export default NotificationPanel;
